import { dataMember, required, typedArray } from 'santee-dcts';
import * as moment from 'moment';
import { isEmpty, some, isNil } from 'lodash';
import { deserializers } from '../core/deserializers';
import { TestResult } from './test-result';
import { OnScreenResults } from '../on-screen-results/on-screen-results';
import { siteDisplayNameRenderer } from '../renderer/site-display-name-renderer';

export type Flags = { [index: string]: string };

export class Flag {

    public readonly name: string;

    public readonly category: string;

    constructor(name: string, category: string) {
        this.name = name;
        this.category = category;
    }

    public get nameUpperCase(): string {
        return this.name ? this.name.toUpperCase() : '';
    }

    public get fullName(): string {
        return `${this.nameUpperCase} - ${this.category}`;
    }
}

export class LabTestResult extends OnScreenResults {

    @dataMember()
    @required()
    public testId: number;

    @dataMember()
    @required()
    public labTestId: number;

    @dataMember()
    @required()
    public zcTestCode: string;

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    @required({ nullable: true })
    public visitCollectionDate: moment.Moment;

    public get visitCollectionDateValue(): number {
        if (!(moment.isMoment(this.visitCollectionDate) && this.visitCollectionDate.isValid())) {
            return null;
        }
        return this.visitCollectionDate.valueOf();
    }

    @dataMember()
    @required()
    public dayOfStudy: number;

    @dataMember()
    @required()
    public ctSubjectId: number;

    @dataMember()
    @required()
    public accessionId: number;

    @dataMember()
    @required({ nullable: true })
    public siteNumber: string;

    @dataMember()
    @required({ nullable: true })
    public resultFormat: string;

    @dataMember()
    @required()
    public visitTestId: number;

    @dataMember()
    @required({ nullable: true })
    public visitCode: string;

    @dataMember()
    public visitTitle: string;

    @dataMember()
    @required({ nullable: true })
    public accessionNumber: string;

    @dataMember()
    @required({ nullable: true })
    public siResult: TestResult;

    @dataMember()
    @required({ nullable: true })
    public convResult: TestResult;

    @dataMember()
    @required({ nullable: true })
    public sponsorFlag: string;

    @dataMember()
    @required({ nullable: true })
    public alertFlag: string;

    @dataMember()
    @required({ nullable: true })
    public deltaFlag: string;

    @dataMember()
    @required({ nullable: true })
    public exclusionFlag: string;

    @dataMember()
    @required()
    public test: string;

    @dataMember()
    @required({ nullable: true })
    public testGroup: string;

    @dataMember()
    @required()
    public isOptional: boolean;

    @dataMember()
    public comments: string;

    @dataMember()
    public hasSubjectDataRevision: boolean;

    @dataMember()
    public hasVisitDataRevision: boolean;

    @dataMember()
    public hasResultDataRevision: boolean;

    @dataMember()
    @required({ nullable: true })
    public testSequence: number;

    public subjectSerialNumber: number;

    public showResultsInSI: boolean;

    public exportSortId: number;

    public subjectSiteId: number;

    public trendFlags: Flag[];

    public get currentResult(): TestResult {
        return this.showResultsInSI ? this.siResult || null : this.convResult || null;
    }

    public get uniqLabTestIdKey(): string {
        return `${this.testId} : ${this.testGroup} : ${this.zcTestCode}`;
    }

    public get visitColumnKey(): string {
        return `${this.visitCollectionDate} : ${this.accessionId}`;
    }

    public get unit(): string {
        if (this.currentResult) {
            return this.currentResult.unit;
        }

        return null;
    }

    public get testNameWithUnit(): string {
        if (this.currentResult) {
            const unitPart = this.unit ? `(${this.unit})` : '';
            return `${this.test} ${unitPart}`;
        }

        return this.test;
    }

    public get protocolFlags(): Flags {
        let flags: Flags = {};
        if (this.sponsorFlag) {
            flags[this.sponsorFlag] = this.sponsorFlag;
        }
        return flags;
    }

    public get standardFlags(): Flags {
        let flags: Flags = {};
        if (this.alertFlag) {
            flags[this.alertFlag] = this.alertFlag;
        }
        if (this.deltaFlag) {
            flags[this.deltaFlag] = this.deltaFlag;
        }
        if (this.exclusionFlag) {
            flags[this.exclusionFlag] = this.exclusionFlag;
        }
        return flags;
    }

    public get flags(): Flag[] {
        let flags: Flag[] = [];
        if (this.alertFlag) {
            flags.push(new Flag(this.alertFlag, 'Alert Flag'));
        }
        if (this.deltaFlag) {
            flags.push(new Flag(this.deltaFlag, 'Delta Flag'));
        }
        if (this.exclusionFlag) {
            flags.push(new Flag(this.exclusionFlag, 'Visit Specific Flag'));
        }
        if (this.sponsorFlag) {
            flags.push(new Flag(this.sponsorFlag, 'Non-Visit Specific Flag'));
        }
        return flags;
    }

    public get isFlagged(): boolean {
        const flags = [this.sponsorFlag, this.alertFlag, this.deltaFlag, this.exclusionFlag];
        return some(flags, flag => !isEmpty(flag));
    }

    public get resultSequence(): number {
        return this.currentResult ? this.currentResult.sequence || 0 : 0;
    }

    public get isCombinedResult(): boolean {
        return this.resultFormat === 'F';
    }

    public get visitName(): string {
        return !isNil(this.visitDescription) && !isEmpty(this.visitDescription) ? this.visitDescription : this.visitTitle;
    }
}

export class LabTestResultsReadModel {
    @dataMember()
    @required()
    @typedArray(LabTestResult)
    public results: LabTestResult[];
}
